import TitledPanel from '../core/TitledPanel';
import LanguageSelector from '../core/LanguageSelector';
import Errored from '../core/Errored';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import PropTypes from 'prop-types';
import React from 'react';
import AddButton from '../core/AddButton';
import CancelButton from '../core/CancelButton';
import Help from '../core/Help';
import InAppBar from '../core/InAppBar';
import PrimaryAddButton from '../core/PrimaryAddButton';
import { ProjectConsumer } from '../core/providers/ProjectProvider';
import SearchSelect from '../core/SearchSelect';
import { globalStyles as styles } from '../core/style/gstheme';
import { checkValidForDatatype, getGraphFromProject, getProjectFromGraph, makeCancelable, computeConceptIcon, computeLabelFromType } from '../core/utils/utils';
import GS from '../grafsync/grafsync';
import ProjectLink from '../vocabulary/core/ProjectLink';
import ComponentLink from './ComponentLink';
import SearchLinkedData from '../core/SearchLinkedData';

// The list of all possible label properties
const PROPS_LIST = ["prefLabel", "label", "literalForm", "title"];

class FindOntologyRelatedResourceDialog extends React.Component {

    constructor(props) {
        super(props);
        const allowEmpty = "inComponent" in props.info
            || (props.range && (
                props.range.includes("http://www.w3.org/2004/02/skos/core#ConceptScheme")
                || props.range.includes("http://www.w3.org/2004/02/skos/core#Collection")
                || props.range.includes("http://www.w3.org/2004/02/skos/core#OrderedCollection")
                || props.range.includes("https://grafsync.graphifi.com/schema/gs/inDataset"))
            ) ?
            true
            :
            false;
        this.state = {
            open: false,
            offerExternal: false,
            tabValue: 0,
            allowEmpty,
            lang: props.viewLang || props.defaultLang,
            externalDuplicate: false,
            text: "",
            componentTitle: "",
            componentType: "",
            componentProjectLangs: [],
            componentLargeDataSettings: false
        };
        this.cancellable = null;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    handleClickOpen = async () => {
        try {
            const { inComponent, inComponentProject } = this.props.info;
            GS.handle("load-info");
            if (inComponent) {
                const componentGraphIRI = inComponentProject ? getGraphFromProject(inComponentProject) : null;
                // Are we targetting a different graph?
                const isOtherProject = inComponent && componentGraphIRI && componentGraphIRI !== this.props.graph;
                const graph = componentGraphIRI || this.props.graph;
                const componentProject = getProjectFromGraph(graph);
                // If we're targetting a component we load some information about it
                await this.loadData([inComponent, componentProject], graph).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                if (!GS.isLoaded(inComponent, graph) || (isOtherProject && !GS.isLoaded(componentProject, graph))) {
                    this.setState({ isErrored: { message: "Cannot load component to search. Please ensure you have permisisons for the project that is being targetted." } });
                }
                else {
                    const componentData = GS.data(inComponent, graph);
                    const componentProjectData = GS.data(componentProject, graph);
                    const componentType = componentData.type;
                    const componentTitle = componentData.title && componentData.title[this.props.viewLang] ?
                        componentData.title[this.props.viewLang]
                        :
                        componentData.title && componentData.title[componentProjectData.defaultLang] ?
                            componentData.title[componentProjectData.defaultLang]
                            :
                            inComponent;
                    this.setState({ open: true, componentType, componentTitle, componentProjectLangs: [...componentProjectData.projectLang], largeDataSettings: componentProjectData.largeDataSettings });
                }
            }
            else {
                this.setState({ open: true });
            }
        }
        catch (e) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({ isErrored: e });
        }
    };

    loadData(iris, graph) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, graph, true));
        return this.cancellable;
    }

    changeLanguage = (lang) => this.setState({ lang });

    computeSearch = (value, lang, projectIRI) => {
        const { searchClasses, mode, associatedProject } = this.props;
        const { inComponent, inComponentProject } = this.props.info;
        const { componentType } = this.state;
        GS.handle("search-resources");
        const componentGraphIRI = inComponentProject ? getGraphFromProject(inComponentProject) : null;
        const tabValue = this.state.tabValue;
        const isOtherProject = inComponent && componentGraphIRI && componentGraphIRI !== this.props.graph;

        const currentProjectOnly = !isOtherProject && (tabValue !== 1 || !associatedProject || associatedProject.length === 0);
        // Compute which graph(s) we're searching
        const projectGraphs = currentProjectOnly ?
            [getGraphFromProject(projectIRI)]
            :
            tabValue === 1 ?
                associatedProject.map(ap => getGraphFromProject(ap))
                :
                [componentGraphIRI];

        // This means we're searching for taxonomies, datasts, etc
        const isComponentSearch = tabValue === 0 &&
            (searchClasses.includes("http://www.w3.org/2004/02/skos/core#ConceptScheme")
                || searchClasses.includes("http://www.w3.org/2004/02/skos/core#Collection")
                || searchClasses.includes("http://www.w3.org/2004/02/skos/core#OrderedCollection")
                || searchClasses.includes("https://grafsync.graphifi.com/schema/gs/Dataset")
            );

        // For linked projects we're always searching taxonomies
        // By default we only search for preferred labels but this can be overridden in the mode with linkedSearchLabel
        // which is necessary for searching for XL labels too when editing XL labels
        const labelProp = isComponentSearch ?
            "http://purl.org/dc/terms/title"
            :
            tabValue === 1 || inComponent ?
                mode.linkedSearchLabel ?
                    mode.linkedSearchLabel
                    :
                    inComponent && componentType === "Dataset" ?
                        "http://www.w3.org/2000/01/rdf-schema#label"
                        :
                        "http://www.w3.org/2004/02/skos/core#prefLabel"
                :
                mode.searchLabel;

        // For linked projects always inScheme
        const searchInProp = mode.searchIn;

        // If there is a componentIRI we're targetting a taxonomy/collection
        // By default we only search for concepts but this can be overridden in the mode with linkedSearchType
        // which is necessary for searching for XL labels too when editing XL labels
        const searchType = tabValue === 1 || inComponent ?
            mode.linkedSearchType ?
                mode.linkedSearchType
                :
                inComponent && componentType === "Dataset" ?
                    "https://grafsync.graphifi.com/schema/gs/Resource"
                    :
                    "http://www.w3.org/2004/02/skos/core#Concept"
            :
            mode.searchType;

        // Are we targetting searching in a component
        const search = !inComponent ?
            [
                {
                    predicate: searchInProp,
                    ...(currentProjectOnly) && {
                        join: [{
                            predicate: "https://grafsync.graphifi.com/schema/gs/hasComponent",
                            value: projectIRI,
                            reverse: true
                        }]
                    }
                },
                {
                    predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                    value: searchType
                }
            ]
            :
            this.state.componentType === "Dataset" ?
                [
                    {
                        predicate: "https://grafsync.graphifi.com/schema/gs/inDataset",
                        value: inComponent
                    },
                    {
                        predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                        value: "https://grafsync.graphifi.com/schema/gs/Resource"
                    }
                ]
                :
                this.state.componentType === "ConceptScheme" ?
                    [
                        {
                            predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
                            value: inComponent
                        },
                        {
                            predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                            value: "http://www.w3.org/2004/02/skos/core#Concept"
                        }
                    ]
                    :
                    this.state.componentType === "OrderedCollection" ?
                        [
                            {
                                inList: "http://www.w3.org/2004/02/skos/core#memberList",
                                listOf: inComponent
                            },
                            {
                                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                                value: "http://www.w3.org/2004/02/skos/core#Concept"
                            }
                        ]
                        :
                        [
                            {
                                predicate: "http://www.w3.org/2004/02/skos/core#member",
                                value: inComponent,
                                reverse: true
                            },
                            {
                                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                                value: "http://www.w3.org/2004/02/skos/core#Concept"
                            }
                        ];
        let fullSearch =
        {
            search: isComponentSearch ?
                [
                    {
                        predicate: labelProp,
                        ...(value !== "") && {
                            value,
                            isExactMatch: false
                        }
                    }
                ]
                :
                [
                    ...search,
                    {
                        predicate: labelProp,
                        ...(value !== "") && {
                            value,
                            isExactMatch: false
                        }
                    }
                ],
            graph: projectGraphs,
            allowMissingResults: true,
            exclude: this.props.exclude,
            pageSize: 20,
            entailment: labelProp.includes("http://www.w3.org/2004/02/skos/core#prefLabel") ? ["propertyChain"] : [],
            language: lang,
            predicates: [
                "http://www.w3.org/2002/07/owl#deprecated",
                searchInProp,
                "https://grafsync.graphifi.com/schema/gs/iri",
                "https://grafsync.graphifi.com/schema/gs/status",
                "http://www.w3.org/1999/02/22-rdf-syntax-ns#type"
            ].flat()
        };
        if (Array.isArray(labelProp)) {
            fullSearch.predicates = fullSearch.predicates.concat(labelProp);
        }
        else {
            fullSearch.predicates.push(labelProp);
        }
        if (searchClasses.length > 0) {
            fullSearch.search.push({
                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                value: this.props.searchClasses,
                ...(this.props.inferSubclasses) && { inferenceGraph: this.props.associatedProjectGraph },
            });
        }
        // For asymmetric props do not allow in results if prop relationship in other direction already
        if (this.props.searchTypes.includes("Asymmetric") && this.props.source && this.props.property) {
            fullSearch.search.push({
                predicate: this.props.property,
                value: this.props.source,
                isText: false,
                invert: true
            });
        }
        return fullSearch;
    };

    includeResource = (id) => {
        this.props.onIncludeResource(id);
        this.exitFindResource();
    };

    addExternal = () => this.includeResource(this.state.externalIRI);

    exitFindResource = () => this.setState({ open: false, externalDuplicate: false, text: "", tabValue: 0, offerExternal: false });

    declineExternal = () => this.setState({ text: "", offerExternal: false, externalDuplicate: false });

    offerExternal = (iri, valid = true) => {
        if (!valid || iri.includes("graphifi")) {
            this.setState({
                externalDuplicate: false,
                offerExternal: false
            });
        }
        else if (this.props.exclude.includes(iri)) {
            this.setState({
                externalDuplicate: true,
                offerExternal: false
            });
        }
        else {
            const isValid = checkValidForDatatype(iri, "http://www.w3.org/2001/XMLSchema#anyURI");
            this.setState({
                externalDuplicate: false,
                offerExternal: isValid,
                externalIRI: iri
            });
        }
    };

    findPropFromLabels(result) {
        return PROPS_LIST.find(lp => result[lp]);
    }

    updateSearchText = (text) => this.setState({ text, externalDuplicate: false, offerExternal: false });

    changeTab = (tabValue) => this.setState({ tabValue, externalDuplicate: false, offerExternal: false });

    render() {
        const { classes, exclude, associatedProject, linkedData, searchClasses, displayType,
            allowIRI, matching, useWorkflow, mode, info, sourceComponent } = this.props;
        const { offerExternal, externalDuplicate, tabValue, text, componentTitle, isErrored, largeDataSettings,
            componentProjectLangs, lang, allowEmpty, componentType } = this.state;
        const { inComponent } = info;

        if (isErrored) return <Errored data={isErrored} dataTest="fr-error" />;

        const defaultToSearch = (tabValue >= 2 && (!linkedData || (linkedData && linkedData.length === 0)))
            || (tabValue === 1 && (!associatedProject || (associatedProject && associatedProject.length === 0)));

        const tabItems = [
            {
                icon: tabValue === 0 || defaultToSearch ? CheckCircleIcon : RadioButtonUncheckedIcon,
                label: componentTitle && !matching ? componentTitle : "Current Project",
            },
            {
                icon: tabValue === 1 ? CheckCircleIcon : RadioButtonUncheckedIcon,
                disabled: associatedProject.length === 0,
                label: "Linked Projects",
            },
            {
                icon: tabValue === 2 ? CheckCircleIcon : RadioButtonUncheckedIcon,
                disabled: !linkedData || !linkedData.includes("DBpedia"),
                label: "DBpedia"
            },
            {
                icon: tabValue === 3 ? CheckCircleIcon : RadioButtonUncheckedIcon,
                disabled: !linkedData || !linkedData.includes("Wikidata"),
                label: "Wikidata"
            }
        ];

        const isComponentSearch = tabValue === 0 &&
            (searchClasses.includes("http://www.w3.org/2004/02/skos/core#ConceptScheme")
                || searchClasses.includes("http://www.w3.org/2004/02/skos/core#Collection")
                || searchClasses.includes("http://www.w3.org/2004/02/skos/core#OrderedCollection")
                || searchClasses.includes("https://grafsync.graphifi.com/schema/gs/Dataset")
            );

        const searchLabels = Array.isArray(mode.searchLabel) ? mode.searchLabel : [mode.searchLabel];
        const labelAliases = searchLabels.map(sl => GS.getAlias(sl));

        const labelProp = isComponentSearch ?
            "title"
            :
            inComponent ?
                componentType === "Dataset" ?
                    "label"
                    :
                    "prefLabel"
                :
                labelAliases;

        return (
            <ProjectConsumer>
                {({ id: projectIRI, defaultLang }) => (
                    <React.Fragment>
                        <AddButton dataTest="add-relationship" onClick={() => this.handleClickOpen()} title="Add a relationship" />
                        <Dialog
                            maxWidth="lg"
                            fullWidth
                            classes={{
                                paper: classes.paper,
                                paperWidthLg: classes.largeDialog
                            }}
                            className={classes.dialog}
                            open={this.state.open}
                            onClose={this.exitFindCResource}
                            aria-labelledby="find-resource-dialog-title">
                            <DialogTitle disableTypography className={classes.mainHeading} id="find-resource-dialog-title">Find Resource</DialogTitle>
                            <DialogContent style={{ height: 400 }}>
                                <Help textRef="FIND_RESOURCE" />
                                <InAppBar
                                    tabValue={tabValue}
                                    background={false}
                                    onChangeTab={this.changeTab}
                                    tabItems={tabItems} />
                                {tabValue >= 2 && !defaultToSearch ?
                                    tabValue === 2 ?
                                        <div style={{ height: 350 }}>
                                            <SearchLinkedData
                                                exclude={exclude}
                                                key="dbpedia"
                                                mode="dbpedia"
                                                updateSearchText={this.updateSearchText}
                                                chosen={this.includeResource}
                                                initialText={text} />
                                        </div>
                                        :
                                        <div style={{ height: 350 }}>
                                            <SearchLinkedData
                                                key="wikidata"
                                                mode="wikidata"
                                                exclude={exclude}
                                                updateSearchText={this.updateSearchText}
                                                chosen={this.includeResource}
                                                initialText={text} />
                                        </div>
                                    :
                                    <div style={{ height: componentProjectLangs && mode.allowLangSelection ? 270 : 360 }}>
                                        {componentProjectLangs && mode.allowLangSelection &&
                                            <div style={{ marginBottom: 6 }}>
                                                <TitledPanel dataTest="project-language" title="Language">
                                                    <LanguageSelector
                                                        includeLabel={false}
                                                        langs={componentProjectLangs}
                                                        selectedLang={lang}
                                                        onSelect={this.changeLanguage}
                                                        defaultLang={defaultLang} />
                                                </TitledPanel>
                                            </div>
                                        }
                                        <SearchSelect
                                            dataTest="find-relationship"
                                            focus={true}
                                            exclude={exclude}
                                            disableEmptySearches={!inComponent}
                                            allowIRIonNoResults={allowIRI && tabValue === 0}
                                            key={tabValue + "-" + defaultToSearch}
                                            sortByLength={true}
                                            updateSearchText={this.updateSearchText}
                                            associatedProject={tabValue === 1 ? associatedProject : []}
                                            allowEmpty={largeDataSettings ? false : allowEmpty}
                                            maxLength={250}
                                            initialText={text}
                                            search={(value, lang) => this.computeSearch(value, lang, projectIRI)}
                                            listSecondIcon={(result) => computeConceptIcon(result, useWorkflow)}
                                            listSelectionDisplay={(result, lang) => {
                                                const foundProp = this.findPropFromLabels(result);
                                                return foundProp ?
                                                    result[foundProp][lang] ?
                                                        Array.isArray(result[foundProp][lang]) ?
                                                            result[foundProp][lang][0]
                                                            :
                                                            result[foundProp][lang]
                                                        :
                                                        result.id
                                                    :
                                                    result.id;
                                            }}
                                            listDisplay={(result, lang) => {
                                                const foundProp = this.findPropFromLabels(result);
                                                const text = foundProp ?
                                                    result[foundProp][lang] ?
                                                        Array.isArray(result[foundProp][lang]) ?
                                                            result[foundProp][lang].sort().join(" AND ")
                                                            :
                                                            result[foundProp][lang]
                                                        :
                                                        result.id
                                                    :
                                                    result.id;
                                                const isGraphLocked = result.inNamedGraph && GS.isGraphLocked(result.inNamedGraph);
                                                const inMultipleGraphs = result.inNamedGraph && Array.isArray(result.inNamedGraph);
                                                const inAlias = result.type.includes("Resource") ? "inDataset" : "inScheme";
                                                const currentIRI = result.iri && result.iri.length > 0 ?
                                                    result.iri[result.iri.length - 1]
                                                    :
                                                    result.id;
                                                return <div>
                                                    <Typography data-test="result-text" variant="body2">
                                                        <span style={{ wordBreak: 'break-word' }}>
                                                            <Tooltip
                                                                PopperProps={{
                                                                    "data-test": "result-tip"
                                                                }}
                                                                title={currentIRI}>
                                                                <span data-test="result-text-words">{text}</span>
                                                            </Tooltip>
                                                        </span>
                                                    </Typography>
                                                    {displayType &&
                                                        <Typography data-test="caption" variant="caption">{computeLabelFromType(result.type)}</Typography>
                                                    }
                                                    {inMultipleGraphs &&
                                                        <Typography data-test="conflicting-iri" variant="caption">This IRI resource is conflicting as found in multiple projects.</Typography>
                                                    }
                                                    {result[inAlias] && !result[inAlias].includes(sourceComponent) && !inMultipleGraphs &&
                                                        result[inAlias].map(dataset =>
                                                            <ComponentLink
                                                                handleNotAvailable={true}
                                                                key={dataset}
                                                                id={dataset}
                                                                graph={result.inNamedGraph}
                                                                defaultLang={defaultLang} />
                                                        )}
                                                    {tabValue === 1 && !inMultipleGraphs &&
                                                        <ProjectLink
                                                            locked={isGraphLocked}
                                                            margin={true}
                                                            id={getProjectFromGraph(result.inNamedGraph)}
                                                            defaultLang={defaultLang}
                                                            graph={result.inNamedGraph}
                                                            key={result.id} />
                                                    }
                                                </div>;
                                            }}
                                            sortProp={labelProp}
                                            errorText={externalDuplicate ? "This external link already exists." : null}
                                            isClickableResult={(result) => !result.invalid && !result.deprecated
                                                && !(result.inNamedGraph && Array.isArray(result.inNamedGraph))
                                                && !(result.inNamedGraph && GS.isGraphLocked(result.inNamedGraph))}
                                            title="Find a resource"
                                            onClearResults={this.declineExternal}
                                            onNoResults={allowIRI ? this.offerExternal : null}
                                            lang={lang}
                                            chosen={this.includeResource} />
                                    </div>
                                }
                            </DialogContent>
                            <DialogActions className={classes.dialogActions}>
                                <CancelButton onClick={this.exitFindResource} />
                                <PrimaryAddButton title="Add external link" disabled={!offerExternal} onClick={this.addExternal} />
                            </DialogActions>
                        </Dialog>
                    </React.Fragment>
                )}
            </ProjectConsumer>
        );
    }
}

FindOntologyRelatedResourceDialog.defaultProps = {
    exclude: [],
    searchClasses: [],
    associatedProject: [],
    searchTypes: [],
    range: [],
    componentGraphIRI: null,
    allowIRI: true,
    useWorkflow: false,
    inferSubclasses: false
};

FindOntologyRelatedResourceDialog.propTypes = {
    searchClasses: PropTypes.array,
    graph: PropTypes.string.isRequired,
    allowIRI: PropTypes.bool,
    useWorkflow: PropTypes.bool,
    linkedData: PropTypes.array,
    info: PropTypes.object,
    range: PropTypes.array,
    property: PropTypes.string,
    searchTypes: PropTypes.array,
    mode: PropTypes.object.isRequired,
    associatedProject: PropTypes.array,
    inferSubclasses: PropTypes.bool,
    associatedProjectGraph: PropTypes.string,
    onIncludeResource: PropTypes.func.isRequired,
    viewLang: PropTypes.string,
    matching: PropTypes.bool,
    exclude: PropTypes.array,
    classes: PropTypes.object
};

export default withStyles(styles)(FindOntologyRelatedResourceDialog);